import React, { useEffect, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { globeMapLayers } from './GlobeMapLayers';

type GlobeMapProps = {
  lat: number;
  lng: number;
  zoom?: number;
};

type LocationDetails = {
  address: string;
  city: string;
  state: string;
  country: string;
};

const GlobeMap: React.FC<GlobeMapProps> = ({ lat, lng, zoom = 3 }) => {
  const mapRef = useRef<HTMLDivElement | null>(null);
  const mapInstance = useRef<maplibregl.Map | null>(null);
  const popupRef = useRef<maplibregl.Popup | null>(null);
  const [mapError, setMapError] = useState<string | null>(null);

  // Function to get location details via reverse geocoding
  const getLocationDetails = async (latitude: number, longitude: number): Promise<LocationDetails> => {
    try {
      const response = await fetch(
        `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
      );
      const data = await response.json();
      
      return {
        address: data.localityInfo?.administrative?.[0]?.name || 'Address not available',
        city: data.city || data.localityInfo?.administrative?.[2]?.name || 'City not available',
        state: data.principalSubdivision || data.localityInfo?.administrative?.[1]?.name || 'State not available',
        country: data.countryName || data.localityInfo?.administrative?.[0]?.name || 'Country not available'
      };
    } catch (error) {
      console.error('Error fetching location details:', error);
      return {
        address: 'Address not available',
        city: 'City not available',
        state: 'State not available',
        country: 'Country not available'
      };
    }
  };

  useEffect(() => {
    if (!mapRef.current) return;

    console.log('Creating MapLibre GL map for:', lat, lng, zoom);

    // Use the detailed layers from separate file
    let mapStyle: any = { ...globeMapLayers };

    // Try to add globe projection if supported
    try {
      mapStyle.projection = { type: 'globe' };
    } catch (e) {
      console.log('Globe projection not available, using default');
    }

    const mapConfig = {
      container: mapRef.current,
      style: mapStyle,
      center: [lng, lat] as [number, number],
      zoom: zoom,
      pitch: 45,
      bearing: 0,
      minZoom: 3,
      maxZoom: 18
    };

    try {
      const map = new maplibregl.Map(mapConfig);

          map.on('load', () => {
            console.log('Map loaded successfully!');

            // Add navigation controls to top right
            map.addControl(new maplibregl.NavigationControl({
              showCompass: true,
              showZoom: true,
              visualizePitch: true
            }), 'top-right');

            // Add clickable marker at the specified location
            const marker = new maplibregl.Marker({
              color: '#2BB6A3',
              scale: 1.5
            })
              .setLngLat([lng, lat] as [number, number])
              .addTo(map);

            // Create popup instance
            const popup = new maplibregl.Popup({
              closeButton: false,
              closeOnClick: false,
              anchor: 'bottom',
              offset: [0, -10]
            });
            popupRef.current = popup;

            // Close popup when clicking anywhere on the map
            map.on('click', (e) => {
              // Only close if popup is open and click is not on the marker
              if (popup.isOpen()) {
                const markerElement = marker.getElement();
                const clickTarget = e.originalEvent.target as HTMLElement;
                // Check if click is not on the marker or popup
                if (!markerElement.contains(clickTarget) && !clickTarget.closest('.maplibregl-popup')) {
                  popup.remove();
                }
              }
            });

        // Add click event to marker
        marker.getElement().addEventListener('click', async (e) => {
          // Stop event propagation to prevent map click from closing popup immediately
          e.stopPropagation();
          // Fly to the marker location with maximum zoom
          map.flyTo({
            center: [lng, lat] as [number, number],
            zoom: 18,
            pitch: 75,
            bearing: 0,
            speed: 1.5,
            curve: 1.8,
            essential: true
          });
          
          // Show loading state in popup
          const loadingHTML = `
            <div style="min-width: 200px; padding: 1rem 1.25rem; text-align: center;">
              <div style="display: inline-block; animation: spin 1s linear infinite;">🔄</div>
              <div style="margin-top: 0.5rem; font-size: 0.9rem;">Loading location details...</div>
            </div>
          `;
          popup.setHTML(loadingHTML).setLngLat([lng, lat] as [number, number]).addTo(map);
          
          // Fetch location details
          const details = await getLocationDetails(lat, lng);
          
          // Update popup with location details
          const detailsHTML = `
            <div style="min-width: 250px; padding: 1.25rem 1.5rem 1rem 1.5rem;">
              <h3 style="color: #2BB6A3; font-size: 1rem; font-weight: 600;">
                Location Details
              </h3>
              <div style="font-size: 0.9rem">
                <strong>📍 Address:</strong> ${details.address}
              </div>
            </div>
          `;
          popup.setHTML(detailsHTML);
        });

        // Change cursor to pointer on hover
        marker.getElement().style.cursor = 'pointer';

        console.log(`Clickable marker added at ${lat}, ${lng}`);
        
        // Ensure map is properly sized
        setTimeout(() => {
          map.resize();
        }, 100);
      });

      map.on('error', (error) => {
        console.error('MapLibre GL error:', error);
      });

      mapInstance.current = map;

    } catch (error) {
      console.error('Failed to create map:', error);
      setMapError('Failed to load map');
      
      // Fallback: create a simple div with coordinates if map fails
      if (mapRef.current) {
        mapRef.current.innerHTML = `
          <div style="display: flex; align-items: center; justify-content: center; height: 100%; background: #1a1a1a; color: white; font-family: Arial, sans-serif;">
            <div style="text-align: center;">
              <div>📍 Location: ${lat.toFixed(6)}, ${lng.toFixed(6)}</div>
              <div style="margin-top: 10px; font-size: 14px; opacity: 0.7;">MapLibre GL unavailable</div>
            </div>
          </div>
        `;
      }
    }

    return () => {
      if (popupRef.current) {
        popupRef.current.remove();
        popupRef.current = null;
      }
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
      }
      if (mapRef.current) {
        mapRef.current.innerHTML = '';
      }
    };
  }, [lat, lng, zoom]);

  return (
    <div 
      ref={mapRef} 
      className="globe-map" 
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        backgroundColor: 'transparent'
      }}
    >
      {/* User Feedback Card */}
      <div 
        className="globe-feedback-card"
        style={{
          position: 'absolute',
          top: '15px',
          left: '15px',
          background: 'var(--secondary)',
          color: 'white',
          padding: '12px 16px',
          borderRadius: '8px',
          fontSize: '0.85rem',
          fontWeight: '500',
          zIndex: 999,
          backdropFilter: 'blur(8px)',
          border: '1px solid rgba(43, 182, 163, 0.3)',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
          transition: 'opacity 0.3s ease',
          cursor: 'default'
        }}
      >
        <div style={{ marginBottom: '4px' }}>Scroll to zoom</div>
        <div style={{ fontSize: '0.85rem' }}>Click marker for location details</div>
      </div>
    </div>
  );
};

export default GlobeMap;